import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { getAllPublicTrips, getSinglePublicTrip, TripMapProvider } from "src/utils";
import { useIsMobile, useSnackbar, useMapboxRouteAnimation } from 'src/hooks';
import { MobileSwipeableDrawer, Slide, Box, Loader, DrawerHeader, IconButton, ArrowBackIcon } from 'src/components';
import { TripMap, TripDesktopModal, TripsOverview, TripDetails } from '../index';
import UserDisplayCard from './user-display-card';

export default function PublicTripsPortal({footerHeight}) {
  const { userId, tripId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showMobileDrawer, setShowMobileDrawer] = useState(true);
  const [mobileDrawerState, setMobileDrawerState] = useState("half");
  const [mobileDrawerHeight, setMobileDrawerHeight] = useState(null);
  const [hideMobileDrawerMapButton, setHideMobileDrawerMapButton] = useState(false);
  const [isDesktopViewExpanded, setIsDesktopViewExpanded] = useState(true);
  const [isDesktopWideView, setIsDesktopWideView] = useState(false);
  const [currentView, setCurrentView] = useState("Overview");

  const [loadingRoute, setLoadingRoute] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedTripSteps, setSelectedTripSteps] = useState([]);
  const [totalTripKms, setTotalTripKms] = useState(0);
  const [totalTripTime, setTotalTripTime] = useState(0);

  const mapRef = useRef(null);
  const routeRef = useRef([]);
  const { startAnimation, stopAnimation } = useMapboxRouteAnimation(mapRef.current);

  const isMobile = useIsMobile();
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const res = tripId
          ? await getSinglePublicTrip(userId, tripId)
          : await getAllPublicTrips(userId);
        setData(res);
        setSelectedTripSteps(res.trips.flatMap(trip => trip.steps));
      } catch (err) {
        console.error("Failed to fetch public trip data", err);
        openSnackbar("Failed to find trip data", 'error');
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [userId, tripId]);

  const handleViewChange = (newView, tripData = null) => {
    if (tripData) {
      setSelectedTrip(tripData);
      setSelectedTripSteps(tripData.steps);
    }
    if (newView !== "Overview") setHideMobileDrawerMapButton(true)
    if (newView === "Overview") {
      setIsDesktopWideView(false);
      setHideMobileDrawerMapButton(false);
      setSelectedTripSteps(data.trips.flatMap(trip => trip.steps));
    }
    else if (newView === "TripDetails") {
      setIsDesktopWideView(true);
    }
    setCurrentView(newView);
  };

  const getPreviewContent = () => {
    switch (currentView) {
      case "TripDetails":
        return <DrawerHeader 
                  title={selectedTrip?.name} 
                  onBack={() => handleViewChange('Overview')}
                  isLoading={loadingRoute}
                  backText={
                    <IconButton 
                      size="small" 
                      disableRipple>
                          <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  }/>;
      default:
        return <DrawerHeader title="Explore Trips" showBack={false} isLoading={loadingRoute}/>;
    }
  };

  const views = (setDrawerState) => ({
          "Overview": (
            <TripsOverview 
              isPublicPortal={true}
              trips={data.trips} 
              steps={selectedTripSteps}
              handleTripSelect={handleViewChange}
              setDrawerState={setDrawerState}
              setSelectedTripSteps={setSelectedTripSteps}
              totalTripKms={totalTripKms}
              totalTripTime={totalTripTime} />),
          "TripDetails": (
            <Slide direction="left" in={true} mountOnEnter unmountOnExit timeout={500}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <TripDetails 
                  isPublicPortal={true}
                  trip={selectedTrip} 
                  steps={selectedTrip?.steps}
                  setSteps={setSelectedTripSteps}
                  handleViewChange={handleViewChange}
                  transportMode={data?.profile?.transportMode}
                  setDrawerState={setDrawerState}
                  totalTripKms={totalTripKms}
                  totalTripTime={totalTripTime} />
              </Box>
            </Slide>)
  });

  if (loading) return <Loader />;
  if (!data) {
    navigate('/not-found');
    return;
  };

  return (
    <div
      style={{
          position: 'relative',
          height: '100%',
          width: '100vw',
          overflow: 'hidden'
      }}>

      <TripMapProvider>

        <UserDisplayCard 
          isDesktopWideView={isDesktopWideView}
          isDesktopViewExpanded={isDesktopViewExpanded} 
          profile={data?.profile} />

        <div style={{ 
          position: 'absolute', 
          inset: 0, 
          zIndex: 0}}>

          <TripMap 
            mapRef={mapRef}
            routeRef={routeRef}
            startAnimation={startAnimation}
            stopAnimation={stopAnimation}
            isPublicPortal={true}
            steps={selectedTripSteps} 
            transportMode={data?.profile?.transportMode}
            setLoadingRoute={setLoadingRoute}
            setTotalTripKms={setTotalTripKms}
            setTotalTripTime={setTotalTripTime}
            mobileDrawerHeight={isMobile ? mobileDrawerHeight : null}
            mobileDrawerState={mobileDrawerState} />

        </div>

        {isMobile ? (
          <MobileSwipeableDrawer
              open={showMobileDrawer}
              footerHeight={footerHeight}
              defaultState="half"
              hideMapButton={hideMobileDrawerMapButton}
              previewText={getPreviewContent()}
              onStateChange={setMobileDrawerState}
              onHeightChange={setMobileDrawerHeight}>
              
              {({ updateState  }) => (<>
                  {loading ? 
                    <Loader /> 
                  : <>
                      { views(updateState)[currentView] }</>
                  }
              </>)}

          </MobileSwipeableDrawer>
        ) : 
          <div style={{position: 'absolute', top: 0, zIndex: 10, width: '100%', pointerEvents: 'none'}}>
            
            <TripDesktopModal 
              isExpanded={isDesktopViewExpanded} 
              isDesktopWideView={isDesktopWideView}
              setIsExpanded={setIsDesktopViewExpanded}>

              {loading ? 
                  <Loader /> 
              : <>
                  { views(null)[currentView] }</>
              }

            </TripDesktopModal>

          </div>
        }

      </TripMapProvider>

    </div>
  );
};
